import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRouter } from 'next/router';

import { useGetGroup } from '../../../../legacy/groups/hooks';
import ScreenTopBar, {
  TopBarVariants
} from '../../../../legacy/components/ScreenTopBar';
import {
  Avatar,
  AvatarGroup,
  AvatarSizes
} from '../../../../components/Avatar';
import FadeIn from '../../../../legacy/common/components/transition/FadeIn';
import MoreOptionsModal from './components/MoreOptionsModal';
import ShareModal from '../../../components/view-org-layout/ShareModal';
import config from '../../../../config';
import { useGetOrganizationMemberships } from 'api/queries/organizations/memberships';
import { GoHomeFill } from 'react-icons/go';
import { useMediaQuery } from 'theme/useMediaQuery';

interface OrgAdminLayoutProps {
  children: React.ReactNode;
  topMenu?: React.ReactNode;
  goBackDestination?: string;
}

const OrgAdminLayout = ({
  children,
  topMenu,
  goBackDestination
}: OrgAdminLayoutProps) => {
  const { t } = useTranslation(['organizations']);
  const router = useRouter();
  const [isMoreOptionsModalOpen, setIsMoreOptionsModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const { data: organization, isLoading } = useGetGroup({
    groupId: router.query.organizationId as string
  });
  const { data: organizationMemberships } = useGetOrganizationMemberships({
    organizationId: router.query.organizationId as string,
    enabled: !!router.query.organizationId
  });
  const isMobile = useMediaQuery('(max-width: 767px)');

  const goBack = () => {
    if (organization?.isUserAdmin) {
      router.push(
        goBackDestination || `/organizations/${organization.id}/dashboard`
      );
    } else {
      router.push(`/organizations/${organization.id}`);
    }
  };

  const goToHome = () => router.push('/dashboard');

  const toggleMoreOptionsModal = () => {
    setIsMoreOptionsModalOpen((moreOptionsOpen) => !moreOptionsOpen);
  };

  const toggleShareModal = () => {
    setIsShareModalOpen((shareModalOpen) => !shareModalOpen);
  };

  if (isLoading) {
    return null;
  }

  const BackIcon = () => {
    if (
      router.pathname === '/organizations/[organizationId]/dashboard' &&
      isMobile
    ) {
      return <GoHomeFill />;
    }
    return null;
  };

  return (
    <>
      <ScreenTopBar
        onBack={
          router.pathname !== '/organizations/[organizationId]/dashboard'
            ? goBack
            : isMobile && goToHome
        }
        BackIcon={BackIcon}
        title={t('title')}
        subtitle={organization?.name}
        variant={TopBarVariants.Big}
        onMenuClick={toggleMoreOptionsModal}
        icon={organization?.badgeImageUrl}
        wideContent
      >
        {topMenu}
        <div className="items-center pr-5 hidden md:flex">
          <AvatarGroup
            additionalCountSize={AvatarSizes.Md}
            additionalCount={
              organization?.metadata.memberCount > 5
                ? organization?.metadata.memberCount -
                  organization?.metadata.memberPreview.length
                : 0
            }
          >
            {organization?.metadata.memberPreview.map((member) => (
              <Avatar
                src={member.photoUrl}
                key={member.id}
                size={AvatarSizes.Md}
              />
            ))}
          </AvatarGroup>
        </div>
      </ScreenTopBar>
      <div className="-mt-[41px] md:mt-[61px] pt-7">
        <FadeIn>{children}</FadeIn>
      </div>
      <MoreOptionsModal
        toggleShareModal={toggleShareModal}
        toggleMoreOptions={toggleMoreOptionsModal}
        visible={isMoreOptionsModalOpen}
        orgId={organization?.id}
        isAdmin={organization?.isUserAdmin}
        organizationMemberships={organizationMemberships}
      />
      <ShareModal
        visible={isShareModalOpen}
        onClose={toggleShareModal}
        shareLink={`${config.SITE_URL}organizations/${organization?.id}`}
      />
    </>
  );
};

export default OrgAdminLayout;
